import { useCallback, useEffect, useState, type FormEvent } from "react";

import { apiClient, type Quota } from "../../api/client";
import { errorMessage } from "./errors";

export function QuotaManager() {
  const [quotas, setQuotas] = useState<Quota[]>([]);
  const [subjectType, setSubjectType] = useState<Quota["subject_type"]>("department");
  const [subjectId, setSubjectId] = useState("");
  const [period, setPeriod] = useState<Quota["period"]>("monthly");
  const [requests, setRequests] = useState("");
  const [tokens, setTokens] = useState("");
  const [images, setImages] = useState("");
  const [cost, setCost] = useState("");
  const [concurrency, setConcurrency] = useState("2");
  const [rate, setRate] = useState("20");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => setQuotas(await apiClient.listQuotas()), []);
  useEffect(() => {
    load().catch((error: unknown) => setMessage(errorMessage(error)));
  }, [load]);

  async function save(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    try {
      await apiClient.upsertQuota(subjectType, subjectId.trim(), period, {
        request_limit: optionalNumber(requests),
        token_limit: optionalNumber(tokens),
        image_limit: optionalNumber(images),
        cost_limit: cost.trim() || null,
        concurrency_limit: Number(concurrency),
        rate_limit_per_minute: Number(rate),
      });
      setMessage("配额已保存并立即进入当前有效定义。");
      await load();
    } catch (error) {
      setMessage(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  function edit(quota: Quota) {
    setSubjectType(quota.subject_type);
    setSubjectId(quota.subject_id);
    setPeriod(quota.period);
    setRequests(optionalText(quota.request_limit));
    setTokens(optionalText(quota.token_limit));
    setImages(optionalText(quota.image_limit));
    setCost(quota.cost_limit ?? "");
    setConcurrency(String(quota.concurrency_limit));
    setRate(String(quota.rate_limit_per_minute));
  }

  async function disable(quota: Quota) {
    setBusy(true);
    try {
      await apiClient.disableQuota(quota.subject_type, quota.subject_id, quota.period);
      setMessage("配额已停用；历史用量账本保持不变。");
      await load();
    } catch (error) {
      setMessage(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="flow-panel" id="quotas">
      <div className="panel-heading">
        <div>
          <span className="eyebrow">调用前强制执行</span>
          <h2>用户与部门配额</h2>
        </div>
        <span className="phase-badge">{quotas.length} 条定义</span>
      </div>
      {message && (
        <p className="status-banner" role="status">
          {message}
        </p>
      )}
      <div className="admin-two-column">
        <form className="admin-form quota-form" onSubmit={save}>
          <label>
            主体类型
            <select
              onChange={(event) => setSubjectType(event.target.value as Quota["subject_type"])}
              value={subjectType}
            >
              <option value="department">department</option>
              <option value="user">user</option>
            </select>
          </label>
          <label>
            主体 ID
            <input
              onChange={(event) => setSubjectId(event.target.value)}
              required
              value={subjectId}
            />
          </label>
          <label>
            周期
            <select
              onChange={(event) => setPeriod(event.target.value as Quota["period"])}
              value={period}
            >
              <option value="daily">daily</option>
              <option value="monthly">monthly</option>
            </select>
          </label>
          <label>
            请求上限
            <input
              min="0"
              onChange={(event) => setRequests(event.target.value)}
              placeholder="无限制"
              type="number"
              value={requests}
            />
          </label>
          <label>
            Token 上限
            <input
              min="0"
              onChange={(event) => setTokens(event.target.value)}
              placeholder="无限制"
              type="number"
              value={tokens}
            />
          </label>
          <label>
            图片上限
            <input
              min="0"
              onChange={(event) => setImages(event.target.value)}
              placeholder="无限制"
              type="number"
              value={images}
            />
          </label>
          <label>
            成本上限
            <input
              min="0"
              onChange={(event) => setCost(event.target.value)}
              placeholder="无限制"
              step="0.01"
              type="number"
              value={cost}
            />
          </label>
          <label>
            并发上限
            <input
              min="1"
              onChange={(event) => setConcurrency(event.target.value)}
              required
              type="number"
              value={concurrency}
            />
          </label>
          <label>
            每分钟频率
            <input
              min="1"
              onChange={(event) => setRate(event.target.value)}
              required
              type="number"
              value={rate}
            />
          </label>
          <button disabled={busy || !subjectId.trim()} type="submit">
            保存最终定义
          </button>
        </form>
        <div className="quota-list">
          {quotas.map((quota) => (
            <article className={!quota.is_active ? "disabled" : ""} key={quota.id}>
              <div>
                <strong>
                  {quota.subject_type} · {quota.subject_id.slice(0, 8)}
                </strong>
                <span>
                  {quota.period} · 请求 {quota.request_limit ?? "∞"} · Token{" "}
                  {quota.token_limit ?? "∞"}
                </span>
                <small>
                  并发 {quota.concurrency_limit} · 频率 {quota.rate_limit_per_minute}/分钟 · 成本{" "}
                  {quota.cost_limit ?? "∞"}
                </small>
              </div>
              <div>
                <button className="text-button" onClick={() => edit(quota)} type="button">
                  编辑
                </button>
                <button
                  className="text-button"
                  disabled={busy || !quota.is_active}
                  onClick={() => void disable(quota)}
                  type="button"
                >
                  停用
                </button>
              </div>
            </article>
          ))}
          {!quotas.length && <p className="panel-copy">暂无当前范围内的配额定义。</p>}
        </div>
      </div>
    </section>
  );
}

function optionalNumber(value: string): number | null {
  return value.trim() ? Number(value) : null;
}

function optionalText(value: number | null): string {
  return value === null ? "" : String(value);
}
